import React, { useState } from "react";
import { Dayjs } from "dayjs";
import { toast } from "react-toastify";
import {
  useCreateMetaBuilderCampaignMutation,
  useCreateMetaBuilderCampaignStep1Mutation,
  useCreateMetaBuilderAdSetStep2Mutation,
  useCreateMetaBuilderAdStep3Mutation,
  useGetMetaBuilderEligibilityQuery,
  useUpdateMetaBuilderCampaignBudgetScheduleMutation,
  useDeleteDraftCampaignMutation,
  useSaveDraftCampaignMutation,
  useUpdateDraftCampaignMutation
} from "../apis/metaBuilderApi";
import MetaAdsEligibilityModal from "../meta-ads-eligibility-modal";
import MetaAdsSubmittingModal from "../meta-ads-submitting-modal";
import MetaAdsPaymentRequiredModal from "../MetaAdsPaymentRequiredModal";
import { apiBaseUrl } from "../consts/api-url.const";

import mabAdArrow from "../../assets/images/svg/mab-ad-arrow.svg";
import downArrow from "../../assets/images/svg/mab-down-arrow.svg";
import ellipseBg from "../../assets/images/svg/mab-ellipse.svg";
import createIcon from "../../assets/images/svg/mab-summary-create.svg";
import createButtonBg from "../../assets/images/svg/mab-createbttn.svg";
import draftIcon from "../../assets/images/svg/mab-create-draft.svg";
import stepTenIcon from "../../assets/images/svg/mab-compgn-7.svg";
import dropOpenIcon from "../../assets/images/svg/mab-drop-open-7.svg";

type ConfirmationDropdownProps = {
  id: string;
  expandedId: string | null;
  onToggle: (id: string) => void;
  isEditMode?: boolean;
  campaignId?: string | null;
  /** When creating campaign from a restored draft, pass draftId so the draft is deleted after successful creation */
  draftId?: string | null;
  onCampaignCreated?: () => void;
  summary: {
    adName?: string;
    musicUrl?: string;
    musicArtist?: string;
    musicTitle?: string;
    genres?: string[];
    audioFileName?: string;
    isAudioUploading?: boolean;
    audioUploadProgress?: number;
    videos?: Array<{ fileName: string; isUploading: boolean; uploadProgress: number; file?: File; videoUrl?: string }>;
    landingFileName?: string;
    isLandingUploading?: boolean;
    landingUploadProgress?: number;
    country?: string | null;
    interests?: Array<{ id: string; name: string; audience_size?: number; path?: string[] }>;
    adAccountName?: string | null;
    pageName?: string | null;
    instagramName?: string | null;
    pixelName?: string | null;
    audienceAge?: string;
    audienceGender?: string;
    campaignBudget?: string;
    currency?: string;
    campaignStartDate?: string;
    campaignEndDate?: string;
    trackImageUrl?: string | null;
    adAccountId?: string | null;
    pageId?: string | null;
    instagramId?: string | null;
    pixelId?: string | null;
    isScheduled?: "always" | "scheduled";
    timezone?: string;
    startDate?: Dayjs | null;
    startTime?: string;
    endDate?: Dayjs | null;
    endTime?: string;
    adText?: string;
    adHeadline?: string;
  };
};

// Tier countries data (same as in countries-dropdown.tsx)
const TIER_COUNTRIES: Record<"tier-one" | "tier-two" | "tier-three", string[]> = {
  "tier-one": [
    "Australia",
    "Austria",
    "Belgium",
    "Canada",
    "Denmark",
    "Finland",
    "France",
    "Germany",
    "Ireland",
    "Italy",
    "Japan",
    "Netherlands",
    "New Zealand",
    "Norway",
    "Portugal",
    "Spain",
    "Sweden",
    "Switzerland",
    "United Kingdom",
    "United States",
  ],
  "tier-two": [
    "Argentina",
    "Brazil",
    "Chile",
    "Colombia",
    "Costa Rica",
    "Croatia (Hrvatska)",
    "French Guiana",
    "Hong Kong",
    "Iceland",
    "Israel",
    "South Korea",
    "Latvia",
    "Luxembourg",
    "Malta",
    "Mexico",
    "Monaco",
    "Peru",
    "Poland",
    "South Africa",
    "Turkey",
    "Ukraine",
    "Uruguay",
  ],
  "tier-three": [
    "Albania",
    "Algeria",
    "Andorra",
    "Bahrain",
    "Belarus",
    "Bolivia",
    "Bosnia and Herzegovina",
    "Bulgaria",
    "Cyprus",
    "Czech Republic",
    "Dominican Republic",
    "Ecuador",
    "Egypt",
    "El Salvador",
    "Estonia",
    "Greece",
    "Guatemala",
    "Honduras",
    "Hungary",
    "India",
    "Jordan",
    "Kazakhstan",
    "Kuwait",
    "Lebanon",
    "Liechtenstein",
    "Lithuania",
    "Macedonia",
    "Malaysia",
    "Moldova",
    "Morocco",
    "Nicaragua",
    "Oman",
    "Palestinian Territory Occupied",
    "Panama",
    "Paraguay",
    "Philippines",
    "Qatar",
    "Romania",
    "Saudi Arabia",
    "Serbia",
    "Slovakia",
    "Slovenia",
    "Tunisia",
    "United Arab Emirates",
    "Vietnam",
  ],
};

// Comprehensive country name to ISO code mapping
const COUNTRY_TO_ISO: Record<string, string> = {
  // Tier One Countries
  "Australia": "AU",
  "Austria": "AT",
  "Belgium": "BE",
  "Canada": "CA",
  "Denmark": "DK",
  "Finland": "FI",
  "France": "FR",
  "Germany": "DE",
  "Ireland": "IE",
  "Italy": "IT",
  "Japan": "JP",
  "Netherlands": "NL",
  "New Zealand": "NZ",
  "Norway": "NO",
  "Portugal": "PT",
  "Spain": "ES",
  "Sweden": "SE",
  "Switzerland": "CH",
  "United Kingdom": "GB",
  "UK": "GB",
  "United States": "US",
  "USA": "US",
  "United States of America": "US",

  // Tier Two Countries
  "Argentina": "AR",
  "Brazil": "BR",
  "Chile": "CL",
  "Colombia": "CO",
  "Costa Rica": "CR",
  "Croatia (Hrvatska)": "HR",
  "Croatia": "HR",
  "French Guiana": "GF",
  "Hong Kong": "HK",
  "Iceland": "IS",
  "Israel": "IL",
  "South Korea": "KR",
  "Korea": "KR",
  "Latvia": "LV",
  "Luxembourg": "LU",
  "Malta": "MT",
  "Mexico": "MX",
  "Monaco": "MC",
  "Peru": "PE",
  "Poland": "PL",
  "South Africa": "ZA",
  "Turkey": "TR",
  "Ukraine": "UA",
  "Uruguay": "UY",

  // Tier Three Countries
  "Albania": "AL",
  "Algeria": "DZ",
  "Andorra": "AD",
  "Bahrain": "BH",
  "Belarus": "BY",
  "Bolivia": "BO",
  "Bosnia and Herzegovina": "BA",
  "Bulgaria": "BG",
  "Cyprus": "CY",
  "Czech Republic": "CZ",
  "Dominican Republic": "DO",
  "Ecuador": "EC",
  "Egypt": "EG",
  "El Salvador": "SV",
  "Estonia": "EE",
  "Greece": "GR",
  "Guatemala": "GT",
  "Honduras": "HN",
  "Hungary": "HU",
  "India": "IN",
  "Jordan": "JO",
  "Kazakhstan": "KZ",
  "Kuwait": "KW",
  "Lebanon": "LB",
  "Liechtenstein": "LI",
  "Lithuania": "LT",
  "Macedonia": "MK",
  "Malaysia": "MY",
  "Moldova": "MD",
  "Morocco": "MA",
  "Nicaragua": "NI",
  "Oman": "OM",
  "Palestinian Territory Occupied": "PS",
  "Panama": "PA",
  "Paraguay": "PY",
  "Philippines": "PH",
  "Qatar": "QA",
  "Romania": "RO",
  "Saudi Arabia": "SA",
  "Serbia": "RS",
  "Slovakia": "SK",
  "Slovenia": "SI",
  "Tunisia": "TN",
  "United Arab Emirates": "AE",
  "UAE": "AE",
  "Vietnam": "VN",
  "China": "CN",
  "Pakistan": "PK",
};

// Country name to ISO code mapping
// Helper function to convert ISO codes to country names
const getCountryNamesFromISO = (isoCodes: string[]): string[] => {
  // Create reverse mapping: ISO -> Country Name
  const ISO_TO_COUNTRY: Record<string, string> = {};
  Object.entries(COUNTRY_TO_ISO).forEach(([name, iso]) => {
    ISO_TO_COUNTRY[iso] = name;
  });

  return isoCodes.map(iso => ISO_TO_COUNTRY[iso] || iso).filter(Boolean);
};

// Helper function to convert tier names to actual country names
const getCountryNames = (countryName: string | null | undefined): string[] => {
  if (!countryName) return [];

  // Handle comma-separated list of countries
  const countryNames = countryName.split(',').map(name => name.trim());

  const result: string[] = [];

  // Create reverse mapping: ISO -> Country Name (for converting codes like "US" to "United States")
  const ISO_TO_COUNTRY: Record<string, string> = {};
  Object.entries(COUNTRY_TO_ISO).forEach(([name, iso]) => {
    ISO_TO_COUNTRY[iso] = name;
  });

  countryNames.forEach(name => {
    // Check if it's a tier-based selection
    if (name.includes("Tier")) {
      // Expand tier selections to individual country names
      if (name.includes("Tier One and Two")) {
        // Combine tier-one and tier-two
        const tierOneCountries = TIER_COUNTRIES["tier-one"];
        const tierTwoCountries = TIER_COUNTRIES["tier-two"];
        const allCountries = [...tierOneCountries, ...tierTwoCountries];

        // Add country names (not ISO codes)
        allCountries.forEach(country => {
          if (!result.includes(country)) {
            result.push(country);
          }
        });
      } else if (name.includes("Tier One")) {
        const countries = TIER_COUNTRIES["tier-one"];
        countries.forEach(country => {
          if (!result.includes(country)) {
            result.push(country);
          }
        });
      } else if (name.includes("Tier Two")) {
        const countries = TIER_COUNTRIES["tier-two"];
        countries.forEach(country => {
          if (!result.includes(country)) {
            result.push(country);
          }
        });
      } else if (name.includes("Tier Three")) {
        const countries = TIER_COUNTRIES["tier-three"];
        countries.forEach(country => {
          if (!result.includes(country)) {
            result.push(country);
          }
        });
      }
    } else {
      // Check if it's an ISO country code (like "US", "GB", "DE")
      // ISO codes are typically 2 letters, country names are longer
      if (name.length === 2 && name === name.toUpperCase() && ISO_TO_COUNTRY[name]) {
        // Convert ISO code to country name
        const fullName = ISO_TO_COUNTRY[name];
        if (!result.includes(fullName)) {
          result.push(fullName);
        }
      } else {
        // It's already a country name, add it directly
        if (!result.includes(name)) {
          result.push(name);
        }
      }
    }
  });

  return result;
};

const getCountryCode = (countryName: string | null | undefined): string[] => {
  if (!countryName) return [];

  // Handle comma-separated list of countries
  const countryNames = countryName.split(',').map(name => name.trim());

  const isoCodes: string[] = [];

  countryNames.forEach(name => {
    // Check if it's a tier-based selection
    if (name.includes("Tier")) {
      // Expand tier selections to individual countries
      if (name.includes("Tier One and Two")) {
        // Combine tier-one and tier-two
        const tierOneCountries = TIER_COUNTRIES["tier-one"];
        const tierTwoCountries = TIER_COUNTRIES["tier-two"];
        const allCountries = [...tierOneCountries, ...tierTwoCountries];

        // Convert each country name to ISO code
        allCountries.forEach(country => {
          const iso = COUNTRY_TO_ISO[country];
          if (iso && !isoCodes.includes(iso)) {
            isoCodes.push(iso);
          }
        });
      } else if (name.includes("Tier One")) {
        const countries = TIER_COUNTRIES["tier-one"];
        countries.forEach(country => {
          const iso = COUNTRY_TO_ISO[country];
          if (iso && !isoCodes.includes(iso)) {
            isoCodes.push(iso);
          }
        });
      } else if (name.includes("Tier Two")) {
        const countries = TIER_COUNTRIES["tier-two"];
        countries.forEach(country => {
          const iso = COUNTRY_TO_ISO[country];
          if (iso && !isoCodes.includes(iso)) {
            isoCodes.push(iso);
          }
        });
      } else if (name.includes("Tier Three")) {
        const countries = TIER_COUNTRIES["tier-three"];
        countries.forEach(country => {
          const iso = COUNTRY_TO_ISO[country];
          if (iso && !isoCodes.includes(iso)) {
            isoCodes.push(iso);
          }
        });
      }
    } else {
      // Individual country - look up its ISO code
      const iso = COUNTRY_TO_ISO[name];
      if (iso && !isoCodes.includes(iso)) {
        isoCodes.push(iso);
      }
    }
  });

  return isoCodes;
};

// Build campaign payload from summary data
const buildCampaignPayload = (summary: ConfirmationDropdownProps["summary"]) => {
  // Parse age range
  let ageMin = 18;
  let ageMax = 65;
  if (summary.audienceAge && summary.audienceAge !== "All age") {
    // Parse age range like "18-25" or "25-45"
    const ageMatch = summary.audienceAge.match(/(\d+)-(\d+)/);
    if (ageMatch) {
      ageMin = parseInt(ageMatch[1], 10);
      ageMax = parseInt(ageMatch[2], 10);
      ageMax = parseInt(ageMatch[2], 10);
    }
  }

  // Parse gender - only send specific gender, not "All" or "Other"
  let genders: number[] | undefined = undefined;
  if (summary.audienceGender === "Male") {
    genders = [1];
  } else if (summary.audienceGender === "Female") {
    genders = [2];
  }
  // For "All" or "Other", genders remains undefined

  // Map interests - Facebook requires both id and name
  const interests = (summary.interests || []).map((interest) => ({
    id: interest.id,
    name: interest.name,
  }));

  // Map countries
  const countries = getCountryCode(summary.country);

  // Validate that at least one country is selected
  if (!countries || countries.length === 0) {
    toast.error("Please select at least one country for your audience targeting.");
    return;
  }

  // Build targeting spec with advantage_audience flag
  const targetingSpec: any = {
    age_min: ageMin,
    age_max: ageMax,
    geo_locations: {
      countries,
    },
    // Facebook requires targeting_automation with advantage_audience flag
    targeting_automation: {
      advantage_audience: 0, // 1 = enabled, 0 = disabled
    },
  };

  // Only add genders if it's defined (Male or Female)
  if (genders !== undefined) {
    targetingSpec.genders = genders;
  }

  // Add interests if any are selected
  if (interests.length > 0) {
    targetingSpec.interests = interests;
  }

  // Format adAccountId with "act_" prefix if not already present
  const formatAdAccountId = (id: string | null | undefined): string => {
    if (!id) return "";
    // If it already starts with "act_", return as is
    if (id.startsWith("act_")) return id;
    // Otherwise, add "act_" prefix
    return `act_${id}`;
  };

  // Build payload
  const payload: any = {
    campaignName: summary.adName || "",
    objective: "OUTCOME_ENGAGEMENT",
    adAccountId: formatAdAccountId(summary.adAccountId),
    budget: {
      dailyBudget: parseFloat(summary.campaignBudget || "4.35"),
      budgetType: "DAILY",
    },
    targeting: targetingSpec, // Use the targeting spec with advantage_audience flag
    // Only include schedule if campaign is scheduled
    ...(summary.isScheduled === "scheduled" && {
      schedule: {
        isScheduled: true,
        startDate: summary.startDate ? summary.startDate.format("YYYY-MM-DD") : null,
        startTime: summary.startTime || null,
        endDate: summary.endDate ? summary.endDate.format("YYYY-MM-DD") : null,
        endTime: summary.endTime || null,
        timezone: summary.timezone || "UTC",
      }
    }),
    adName: summary.adName ? `${summary.adName} Ad` : "Spotify Track Ad",
    creative: {
      linkUrl: summary.musicUrl || "",
      headline: summary.adHeadline || undefined,
      description: summary.adText || undefined,
    },
    language: "1",
    // Add DSA beneficiary if pageId is available (required for EU ads)
    ...(summary.pageId && {
      dsa_beneficiary: {
        id: summary.pageId
      }
    })
  };

  // Add optional fields
  if (summary.pageId) {
    payload.pageId = summary.pageId;
  }

  // Use the Instagram ID shown in the confirmation dropdown (summary) for all API calls
  if (summary.instagramId) {
    payload.instagramAccountId = summary.instagramId;
  }

  if (summary.pixelId) {
    payload.pixelId = summary.pixelId;
  }

  // Use user input from Advanced Settings only
  if (summary.adText || summary.adHeadline) {
    payload.adText = {
      headlineTop: summary.adText || "",
      headlineBottom: summary.adHeadline || "",
    };
  }

  return payload;
};

const ConfirmationDropdown: React.FC<ConfirmationDropdownProps> = ({
  id,
  expandedId,
  onToggle,
  isEditMode = false,
  campaignId = null,
  draftId = null,
  summary,
}) => {
  const isOpen = expandedId === id;
  const [checkbox1, setCheckbox1] = useState(false);
  const [checkbox2, setCheckbox2] = useState(false);
  const [checkbox3, setCheckbox3] = useState(false);
  const [isSavingDraft, setIsSavingDraft] = useState(false);

  // Original single-step API (kept for backward compatibility)
  const [createCampaign, { isLoading: isCreatingLegacy }] = useCreateMetaBuilderCampaignMutation();

  // New step-by-step APIs
  const [createCampaignStep1, { isLoading: isCreatingStep1 }] = useCreateMetaBuilderCampaignStep1Mutation();
  const [createAdSetStep2, { isLoading: isCreatingStep2 }] = useCreateMetaBuilderAdSetStep2Mutation();
  const [createAdStep3, { isLoading: isCreatingStep3 }] = useCreateMetaBuilderAdStep3Mutation();

  // Update campaign mutation
  const [updateCampaignBudgetSchedule, { isLoading: isUpdating }] = useUpdateMetaBuilderCampaignBudgetScheduleMutation();

  // Delete draft mutation
  const [deleteDraftCampaign] = useDeleteDraftCampaignMutation();

  // Save and update draft mutations
  const [saveDraftCampaign] = useSaveDraftCampaignMutation();
  const [updateDraftCampaign] = useUpdateDraftCampaignMutation();

  // Video upload state
  const [isUploadingVideos, setIsUploadingVideos] = useState(false);

  // Progress tracking
  const [progress, setProgress] = useState(0);
  const [currentStep, setCurrentStep] = useState("");

  // New submitting modal state
  interface SubmissionStep {
    label: string;
    completed: boolean;
  }

  const [showSubmittingModal, setShowSubmittingModal] = useState(false);
  const [isSubmissionSuccess, setIsSubmissionSuccess] = useState(false);
  const [isSubmissionError, setIsSubmissionError] = useState(false);
  const [submissionErrorMessage, setSubmissionErrorMessage] = useState("");
  const [successInstagramUsername, setSuccessInstagramUsername] = useState<string | undefined>();
  const [successCampaignId, setSuccessCampaignId] = useState<string | undefined>();
  const [submissionMode, setSubmissionMode] = useState<"create" | "draft" | "update">("create");
  const [submissionSteps, setSubmissionSteps] = useState<SubmissionStep[]>([
    { label: "Checking account eligibility", completed: false },
    { label: "Checking payment method on the ad account", completed: false },
    { label: "Creating your campaign", completed: false },
    { label: "Verifying tracking setup", completed: false },
    { label: `Uploading your videos (1/${summary.videos?.length || 0})`, completed: false },
    { label: "Creating your ads", completed: false },
    { label: "Setting your audience targeting", completed: false },
    { label: "Submitting to Facebook & Instagram", completed: false },
  ]);

  // Helper function to update a specific step
  const updateStep = (stepIndex: number, completed: boolean, progressValue: number) => {
    setSubmissionSteps(prev => {
      const newSteps = [...prev];
      newSteps[stepIndex].completed = completed;
      return newSteps;
    });
    setProgress(progressValue);
  };

  // Eligibility modal state
  const [showEligibilityModal, setShowEligibilityModal] = useState(false);
  const [eligibilityData, setEligibilityData] = useState<{
    eligible: boolean;
    reason: string | null;
    checks: any;
    recommendations: any[];
  } | null>(null);

  // Payment required modal state
  const [showPaymentRequiredModal, setShowPaymentRequiredModal] = useState(false);

  const isCreating = isCreatingStep1 || isUploadingVideos || isCreatingStep2 || isCreatingStep3 || isUpdating;

  // Helper function to build targeting object
  const buildTargetingObject = (summary: ConfirmationDropdownProps["summary"]) => {
    const targeting: any = {};

    // Handle countries - geo_locations is required by Facebook
    if (summary.country) {
      const countries = getCountryCode(summary.country);
      if (countries.length > 0) {
        targeting.geo_locations = {
          countries: countries,
        };
      } else {
        // Default to US if no country mapping found
        targeting.geo_locations = {
          countries: ["US"],
        };
      }
    } else {
      // Default to US if no country selected
      targeting.geo_locations = {
        countries: ["US"],
      };
    }

    // Handle age
    if (summary.audienceAge && summary.audienceAge !== "All age") {
      const ageRange = summary.audienceAge.split("-").map(Number);
      if (ageRange.length === 2) {
        targeting.age_min = ageRange[0];
        targeting.age_max = ageRange[1];
      }
    }

    // Handle gender - only send for Male or Female, not for "All" or "Other"
    if (summary.audienceGender === "Male") {
      targeting.genders = [1];
    } else if (summary.audienceGender === "Female") {
      targeting.genders = [2];
    }
    // For "All" or "Other", do not add genders parameter

    // Handle interests - only send interests that have valid IDs
    if (summary.interests && summary.interests.length > 0) {
      const interestsWithIds = summary.interests.filter((interest) => {
        // Only include interests that have an ID field
        return interest.id && interest.id.length > 0;
      });

      if (interestsWithIds.length > 0) {
        targeting.interests = interestsWithIds.map((interest) => ({
          id: interest.id,
          name: interest.name,
        }));
        console.log(`Sending ${targeting.interests.length} interests with valid IDs`);
      }
    }

    // Add Facebook Advantage+ audience targeting automation
    targeting.targeting_automation = {
      advantage_audience: 0, // 1 = enabled, 0 = disabled
    };

    return targeting;
  };

  // Helper function to build schedule object
  const buildScheduleObject = (summary: ConfirmationDropdownProps["summary"]) => {
    if (summary.isScheduled !== "scheduled") {
      return undefined;
    }

    const schedule: any = {
      isScheduled: true,
    };

    if (summary.startDate) {
      schedule.startDate = summary.startDate.format("YYYY-MM-DD");
    }

    if (summary.startTime) {
      schedule.startTime = summary.startTime;
    }

    if (summary.endDate) {
      schedule.endDate = summary.endDate.format("YYYY-MM-DD");
    }

    if (summary.endTime) {
      schedule.endTime = summary.endTime;
    }

    if (summary.timezone) {
      schedule.timezone = summary.timezone;
    }

    return schedule;
  };

  const handleUpdateCampaign = async () => {
    // Validate checkboxes
    if (!checkbox1 || !checkbox2 || !checkbox3) {
      toast.error("Please confirm all terms and conditions");
      return;
    }

    // Validate required fields
    if (!campaignId) {
      toast.error("Campaign ID is required for update");
      return;
    }

    if (!summary.adName) {
      toast.error("Campaign name is required");
      return;
    }

    if (!summary.campaignBudget) {
      toast.error("Campaign budget is required");
      return;
    }

    try {
      // Build update payload
      const updatePayload: any = {
        campaignId,
        budget: {
          amount: parseFloat(summary.campaignBudget),
        },
      };

      // Add schedule if campaign is scheduled
      if (summary.isScheduled === "scheduled") {
        updatePayload.schedule = {
          startDate: summary.startDate ? summary.startDate.format("YYYY-MM-DD") : undefined,
          startTime: summary.startTime || undefined,
          endDate: summary.endDate ? summary.endDate.format("YYYY-MM-DD") : undefined,
          endTime: summary.endTime || undefined,
          timezone: summary.timezone || undefined,
          isScheduled: true,
        };
      }

      // Call the update mutation
      const result = await updateCampaignBudgetSchedule(updatePayload).unwrap();

      // Check if Meta API update was successful
      if (!result.metaUpdate.success) {
        toast.warning(
          `Campaign updated in database, but Meta API update failed: ${result.metaUpdate.error || "Unknown error"}`
        );
      } else {
        // Show success modal instead of toast
        setSubmissionMode("update");
        setIsSubmissionSuccess(true);
        setSuccessCampaignId(campaignId);
        setShowSubmittingModal(true);
      }

    } catch (error: any) {
      const errorMessage = error?.data?.msg || error?.data?.message || error?.message || "Failed to update campaign";
      toast.error(errorMessage);
      console.error("Campaign update error:", error);
    }
  };

  const handleCreateCampaign = async () => {
    // Validate checkboxes
    if (!checkbox1 || !checkbox2 || !checkbox3) {
      toast.error("Please confirm all terms and conditions");
      return;
    }

    // Validate required fields
    if (!summary.adName) {
      toast.error("Campaign name is required");
      return;
    }

    // Validate ad account and page are required
    if (!summary.adAccountId) {
      toast.error("Ad account is required");
      return;
    }

    // Page ID is required for Facebook ads
    if (!summary.pageId || summary.pageId === "dummy") {
      toast.error("Facebook page is required. Please select a page in Meta Ads Builder.");
      return;
    }

    if (!summary.musicUrl) {
      toast.error("Spotify URL is required");
      return;
    }

    // Reset steps and show modal
    setSubmissionSteps([
      { label: "Checking account eligibility", completed: false },
      { label: "Checking payment method on the ad account", completed: false },
      { label: "Creating your campaign", completed: false },
      { label: "Verifying tracking setup", completed: false },
      { label: `Uploading your videos (1/${summary.videos?.length || 0})`, completed: false },
      { label: "Creating your ads", completed: false },
      { label: "Setting your audience targeting", completed: false },
      { label: "Submitting to Facebook & Instagram", completed: false },
    ]);
    setProgress(0);

    // Reset all submission states before attempting new submission
    setIsSubmissionError(false);
    setSubmissionErrorMessage("");
    setIsSubmissionSuccess(false);
    setSubmissionMode("create"); // Set mode to create for new campaigns

    setShowSubmittingModal(true);

    try {
      // STEP 0: Check Eligibility (15%)
      updateStep(0, true, 15);

      // Use fetch for eligibility check (cleaner than RTK Query for one-time checks)
      const eligibilityResponse = await fetch(`${apiBaseUrl}/api/meta-builder/eligibility`, {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('metaAdsPublicToken')}`,
        },
      });

      if (!eligibilityResponse.ok) {
        let errorMessage = 'Failed to check eligibility. Please try again.';
        try {
          const errorData = await eligibilityResponse.json();
          errorMessage = errorData?.msg || errorData?.message || errorData?.error || errorMessage;
        } catch (e) {
          // If parsing fails, use default message
          console.error('Failed to parse error response:', e);
        }
        toast.error(errorMessage);
        setShowSubmittingModal(false);
        setIsSubmissionError(true);
        setSubmissionErrorMessage(errorMessage);
        return;
      }

      const eligibilityData = await eligibilityResponse.json();

      // Check if not eligible - but allow EU/EEA subscription warning to continue
      const isOnlyEUWarning = !eligibilityData.eligible && eligibilityData.reason === "EU_NO_SUBSCRIPTION";

      if (!eligibilityData.eligible && !isOnlyEUWarning) {
        // Not eligible due to other reasons - stop and show modal
        setEligibilityData(eligibilityData);
        setShowEligibilityModal(true);
        setShowSubmittingModal(false);
        return;
      }

      // STEP 0.5: Check Payment Methods
      // Use adAccountId from summary (user's selection)
      const adAccountIdForPayment = summary.adAccountId?.replace('act_', '') || summary.adAccountId;

      if (adAccountIdForPayment) {
        updateStep(1, true, 25);  // Payment method check = 25% (15 + 10)

        const paymentResponse = await fetch(
          `${apiBaseUrl}/api/meta-builder/payment-methods/${adAccountIdForPayment}`,
          {
            headers: {
              'Authorization': `Bearer ${localStorage.getItem('metaAdsPublicToken')}`,
            },
          }
        );

        if (!paymentResponse.ok) {
          let errorMessage = 'Failed to check payment method. Please try again.';
          try {
            const errorData = await paymentResponse.json();
            errorMessage = errorData?.msg || errorData?.message || errorData?.error || errorMessage;
          } catch (e) {
            console.error('Failed to parse payment error response:', e);
          }
          toast.error(errorMessage);
          setShowSubmittingModal(false);
          setIsSubmissionError(true);
          setSubmissionErrorMessage(errorMessage);
          return;
        }

        const paymentData = await paymentResponse.json();

        // Check if payment method exists
        if (paymentData.hasPaymentMethod) {
          // STEP 1: Create Campaign (only if payment method exists)
          console.log("Step 1: Creating campaign...");

          // Add act_ prefix to adAccountId for payload (use summary - user's selection)
          const adAccountIdWithPrefix = summary.adAccountId?.startsWith('act_')
            ? summary.adAccountId
            : `act_${summary.adAccountId}`;

          const step1Payload = {
            campaignName: summary.adName,
            objective: "OUTCOME_ENGAGEMENT", // Objective for Spotify track promotion - optimized for post engagement
            adAccountId: adAccountIdWithPrefix, // Send with act_ prefix like in summary
            budget: {
              // Always Active = DAILY, Scheduled = SCHEDULED
              type: (summary.isScheduled === "scheduled" ? "SCHEDULED" : "DAILY") as "DAILY" | "SCHEDULED",
              amount: summary.campaignBudget ? parseFloat(summary.campaignBudget) : 5,
            },
            schedule: {
              isScheduled: summary.isScheduled === "scheduled",
              startDate: summary.startDate ? summary.startDate.format("YYYY-MM-DD") : undefined,
              startTime: summary.startTime,
              endDate: summary.endDate ? summary.endDate.format("YYYY-MM-DD") : undefined,
              endTime: summary.endTime,
              timezone: summary.timezone,
            },
          };

          let step1Response;
          try {
            step1Response = await createCampaignStep1(step1Payload).unwrap();
            console.log("Step 1 complete:", step1Response);
            updateStep(2, true, 40);  // Step 1 API = 40% (25 + 15)
          } catch (step1Error: any) {
            console.error("Step 1 failed:", step1Error);

            // Prefer actual API/Meta error over generic msg for notification
            let errorMessage = "Failed to create campaign";
            if (step1Error?.data) {
              errorMessage = step1Error.data.error ||
                            step1Error.data.message ||
                            step1Error.data.error_description ||
                            step1Error.data.msg ||
                            errorMessage;
            } else if (step1Error?.message) {
              errorMessage = step1Error.message;
            } else if (step1Error?.error) {
              errorMessage = step1Error.error;
            }

            // Handle Meta API specific errors
            if (errorMessage.includes('invalid') || errorMessage.includes('Invalid')) {
              errorMessage = `Invalid campaign data: ${errorMessage}`;
            } else if (errorMessage.includes('permission') || errorMessage.includes('Permission')) {
              errorMessage = `Permission denied: ${errorMessage}`;
            } else if (errorMessage.includes('rate') || errorMessage.includes('Rate')) {
              errorMessage = `Rate limit exceeded. Please try again later.`;
            }

            toast.error(errorMessage, {
              autoClose: 8000,
              hideProgressBar: false,
            });
            setShowSubmittingModal(false);
            setIsSubmissionError(true);
            setSubmissionErrorMessage(errorMessage);
            return;
          }

          // STEP 3: Verifying tracking setup
          updateStep(3, true, 55);

          // STEP 4: Uploading videos to Facebook
          console.log("Step 4: Uploading videos...");

          let uploadedVideoIds: string[] = []; // Store uploaded video IDs
          let videoUploadFailed = false; // Track if video upload failed

          // Check if there are videos to upload
          console.log("=== VIDEO UPLOAD CHECK ===");
          console.log("summary.videos:", summary.videos);
          console.log("summary.videos.length:", summary.videos?.length);

          if (summary.videos && summary.videos.length > 0) {
            console.log("=== VIDEO UPLOAD DEBUG ===");
            console.log("Total videos in summary:", summary.videos.length);
            summary.videos.forEach((v, i) => {
              console.log(`Video ${i}:`, {
                fileName: v.fileName,
                hasFile: !!v.file,
                hasVideoUrl: !!v.videoUrl,
                videoUrl: v.videoUrl,
                isUploading: v.isUploading
              });
            });

            try {
              // Prepare files array - both File objects and URLs from drafts
              const videoFilesToUpload: File[] = [];
              const draftVideosToFetch: Array<{ index: number; url: string; fileName: string }> = [];

              // Separate new uploads (File objects) from draft videos (URLs)
              summary.videos.forEach((v, index) => {
                console.log(`Processing video ${index}:`, v);
                if (v.file) {
                  console.log(`→ Has file object, adding to upload list`);
                  videoFilesToUpload.push(v.file);
                } else if (v.videoUrl) {
                  console.log(`→ Has videoUrl, adding to fetch list`);
                  draftVideosToFetch.push({
                    index,
                    url: v.videoUrl,
                    fileName: v.fileName || `video_${index}.mp4`,
                  });
                } else {
                  console.log(`→ No file or videoUrl!`);
                }
              });

              console.log("Video files to upload:", videoFilesToUpload.length);
              console.log("Draft videos to fetch:", draftVideosToFetch.length);

              // Fetch draft videos from S3
              if (draftVideosToFetch.length > 0) {
                console.log(`Fetching ${draftVideosToFetch.length} draft videos from S3...`);
                for (const draftVideo of draftVideosToFetch) {
                  try {
                    const token = localStorage.getItem("metaAdsPublicToken");
                    const response = await fetch(draftVideo.url, {
                      headers: {
                        Authorization: `Bearer ${token}`,
                      },
                    });

                    if (!response.ok) {
                      console.error(`Failed to fetch draft video: ${draftVideo.fileName}`);
                      continue;
                    }

                    const blob = await response.blob();
                    const file = new File([blob], draftVideo.fileName, { type: blob.type || 'video/mp4' });
                    videoFilesToUpload.push(file);
                    console.log(`Successfully fetched draft video: ${draftVideo.fileName}`);
                  } catch (error) {
                    console.error(`Error fetching draft video ${draftVideo.fileName}:`, error);
                  }
                }
              }

              if (videoFilesToUpload.length > 0) {
                setIsUploadingVideos(true);
                const totalVideos = videoFilesToUpload.length;
                const videoUploadErrors = [];

                // Upload videos one by one to show real-time progress
                for (let i = 0; i < videoFilesToUpload.length; i++) {
                  const currentNumber = i + 1;
                  const videoFile = videoFilesToUpload[i];

                  // Update step label to show current video being uploaded
                  setSubmissionSteps(prev => {
                    const newSteps = [...prev];
                    newSteps[4].label = `Uploading your videos (${currentNumber}/${totalVideos})`;
                    return newSteps;
                  });

                  console.log(`Uploading video ${currentNumber}/${totalVideos}: ${videoFile.name}`);

                  try {
                    // Create FormData for single video upload
                    const formData = new FormData();
                    formData.append('adAccountId', adAccountIdWithPrefix);
                    formData.append('campaignId', step1Response.campaign.campaignId);
                    formData.append('videos', videoFile);

                    // Upload single video using fetch API
                    const uploadResponse = await fetch(`${apiBaseUrl}/api/meta-builder/videos/upload`, {
                      method: 'POST',
                      headers: {
                        'Authorization': `Bearer ${localStorage.getItem('metaAdsPublicToken')}`,
                      },
                      body: formData,
                    });

                    if (!uploadResponse.ok) {
                      let errorMessage = `Failed to upload video ${currentNumber}/${totalVideos}`;
                      try {
                        const errorData = await uploadResponse.json();
                        errorMessage = errorData?.msg ||
                                      errorData?.error ||
                                      errorData?.message ||
                                      errorData?.error_description ||
                                      errorMessage;

                        // Add more context for video upload errors
                        if (errorMessage.includes('format') || errorMessage.includes('invalid')) {
                          errorMessage = `Invalid video format: ${errorMessage}`;
                        } else if (errorMessage.includes('size') || errorMessage.includes('too large')) {
                          errorMessage = `Video file too large: ${errorMessage}`;
                        } else if (errorMessage.includes('timeout') || errorMessage.includes('timed out')) {
                          errorMessage = `Video upload timed out. Please try again with smaller files.`;
                        }
                      } catch (e) {
                        console.error('Failed to parse video upload error:', e);
                      }
                      throw new Error(errorMessage);
                    }

                    const uploadData = await uploadResponse.json();
                    console.log(`Video ${currentNumber}/${totalVideos} upload complete:`, uploadData);

                    // Add successfully uploaded video IDs
                    if (uploadData.videoIds && uploadData.videoIds.length > 0) {
                      uploadedVideoIds.push(...uploadData.videoIds);
                      console.log(`✓ Successfully uploaded video ${currentNumber}/${totalVideos}`);
                    }

                    // Track any errors for this video
                    if (uploadData.errors && uploadData.errors.length > 0) {
                      console.warn(`Video ${currentNumber}/${totalVideos} had errors:`, uploadData.errors);
                      videoUploadErrors.push(...uploadData.errors);
                    }

                  } catch (uploadError: any) {
                    console.error(`Error uploading video ${currentNumber}/${totalVideos}:`, uploadError);
                    const errorMessage = uploadError?.message || `Failed to upload video ${currentNumber}/${totalVideos}`;
                    videoUploadErrors.push(errorMessage);

                    // Show error for this specific video but continue with others
                    toast.error(`Video ${currentNumber}/${totalVideos} failed: ${errorMessage}`, {
                      autoClose: 5000,
                      hideProgressBar: false,
                    });
                  }
                }

                // Check if all videos failed
                if (uploadedVideoIds.length === 0) {
                  videoUploadFailed = true;
                  throw new Error('All video uploads failed. Please check the video format and try again.');
                }

                // Show warning if some videos failed
                if (videoUploadErrors.length > 0) {
                  console.warn("Some videos failed to upload:", videoUploadErrors);
                  toast.warning(`Some videos failed: ${videoUploadErrors.join(', ')}`, {
                    autoClose: 10000,
                  });
                } else {
                  console.log(`✓ Successfully uploaded all ${uploadedVideoIds.length} video(s)`);
                }

                // Update final step label to show completion
                setSubmissionSteps(prev => {
                  const newSteps = [...prev];
                  newSteps[4].label = `Uploaded ${uploadedVideoIds.length}/${totalVideos} video(s)`;
                  return newSteps;
                });

                setIsUploadingVideos(false);
              } else {
                console.warn("No valid video files found to upload");
              }
            } catch (uploadError: any) {
              console.error("Error uploading videos:", uploadError);

              // Enhanced error message extraction
              let errorMessage = uploadError?.message || 'Video upload failed';

              if (uploadError?.data) {
                errorMessage = uploadError.data.msg ||
                              uploadError.data.message ||
                              uploadError.data.error ||
                              errorMessage;
              }

              // Add specific context for different error types
              if (errorMessage.includes('network') || errorMessage.includes('Network')) {
                errorMessage = `Network error during video upload. Please check your connection and try again.`;
              } else if (errorMessage.includes('server') || errorMessage.includes('Server')) {
                errorMessage = `Server error during video upload. Please try again later.`;
              } else if (errorMessage.includes('timeout') || errorMessage.includes('timed out')) {
                errorMessage = `Video upload timed out. Please try with smaller files or check your connection.`;
              }

              toast.error(errorMessage, {
                autoClose: 10000,
                hideProgressBar: false,
              });
              setIsUploadingVideos(false);
              setShowSubmittingModal(false);
              setIsSubmissionError(true);
              setSubmissionErrorMessage(errorMessage);

              // Stop campaign creation if video upload failed
              if (uploadedVideoIds.length === 0) {
                return; // Don't continue with next steps
              }

              // Continue with campaign creation even if some videos failed
              console.warn("Continuing with uploaded videos...");
            }
          } else {
            console.log("No videos to upload");
          }

          updateStep(4, true, 70);

          // STEP 2: Create Ad Set
          console.log("Step 2: Creating ad set...");

          // Build targeting object
          const targeting = buildTargetingObject(summary);

          // Check if we need DSA beneficiary (for EU/EEA multi-country targeting)
          // List of EU/EEA country codes that require DSA beneficiary
          const euEeaCountries = [
            "AT", "BE", "BG", "HR", "CY", "CZ", "DK", "EE", "FI", "FR",
            "DE", "GR", "HU", "IS", "IE", "IT", "LV", "LI", "LT", "LU",
            "MT", "NL", "NO", "PL", "PT", "RO", "SK", "SI", "ES", "SE"
          ];

          const countries = getCountryCode(summary.country);
          const hasEuEeaTargeting = countries.some(country => euEeaCountries.includes(country));

          // Build step 2 payload
          const step2Payload: any = {
            campaignId: step1Response.campaign.campaignId,
            adAccountId: adAccountIdWithPrefix,
            adSetName: `${summary.adName} - Ad Set`,
            targeting: targeting,
            schedule: buildScheduleObject(summary),
            budget: {
              dailyBudget: summary.campaignBudget,
            },
            pixelId: summary.pixelId || undefined,
            objective: "OUTCOME_ENGAGEMENT",
            language: undefined,
          };

          // DSA beneficiary/payor: Meta requires for all ads. Page must be selected in Meta Ads Builder.
          if (!summary.pageId) {
            toast.error("Please select a Facebook page in the Meta Ads Builder (Budget step) before creating the campaign.");
            return;
          }
          step2Payload.pageId = summary.pageId;
          step2Payload.dsa_beneficiary = {
            id: summary.pageId,
            type: "PAGE",
            display_name: summary.pageName || "Music Promoter",
          };
          step2Payload.dsa_payor = {
            id: summary.pageId,
            type: "PAGE",
            display_name: summary.pageName || "Music Promoter",
          };

          let step2Response;
          try {
            step2Response = await createAdSetStep2(step2Payload).unwrap();
            console.log("Step 2 complete:", step2Response);
            // Update step 5 after Ad Set creation (70%)
            updateStep(5, true, 70);
          } catch (step2Error: any) {
            console.error("Step 2 failed:", step2Error);

            // Prefer actual API/Meta error over generic msg for notification
            let errorMessage = "Failed to create ad set";
            if (step2Error?.data) {
              errorMessage = step2Error.data.error ||
                            step2Error.data.message ||
                            step2Error.data.error_description ||
                            step2Error.data.msg ||
                            errorMessage;
            } else if (step2Error?.message) {
              errorMessage = step2Error.message;
            }

            // Handle specific targeting errors
            if (errorMessage.includes('targeting') || errorMessage.includes('Targeting')) {
              errorMessage = `Targeting error: ${errorMessage}`;
            } else if (errorMessage.includes('budget') || errorMessage.includes('Budget')) {
              errorMessage = `Budget error: ${errorMessage}`;
            } else if (errorMessage.includes('DSA') || errorMessage.includes('dsa')) {
              errorMessage = `DSA compliance error: ${errorMessage}`;
            }

            toast.error(errorMessage, {
              autoClose: 8000,
              hideProgressBar: false,
            });
            setShowSubmittingModal(false);
            setIsSubmissionError(true);
            setSubmissionErrorMessage(errorMessage);
            return;
          }

          // STEP 3: Create Multiple Ads (one per video)
          console.log("Step 3: Creating multiple ads...");

          let successfulAds = [];
          let failedAds = [];
          const adSetNumber = 1; // Since we're using one ad set, it's always #1

          // Use the Instagram ID from the confirmation dropdown (summary) — this is what we send to the API
          const instagramAccountIdToSend = summary.instagramId ?? undefined;
          // Create one ad per uploaded video
          for (let i = 0; i < uploadedVideoIds.length; i++) {
            const step3Payload = {
              campaignId: step1Response.campaign.campaignId,
              adSetId: step2Response.adSet.adSetId,
              adAccountId: adAccountIdWithPrefix,
              pageId: summary.pageId!,
              instagramAccountId: instagramAccountIdToSend,
              // Format: "CampaignName [Ad Set #1][Ad #1]", "CampaignName [Ad Set #1][Ad #2]"
              adName: `${summary.adName} [Ad Set #${adSetNumber}][Ad #${i + 1}]`,
              creative: {
                imageUrl: summary.trackImageUrl || undefined,
                linkUrl: summary.musicUrl || "",
                message: summary.adText || undefined,
              },
              adText: {
                headlineTop: summary.adText || undefined,
                headlineBottom: summary.adHeadline || undefined,
                message: summary.adText || undefined,
              },
              // Track information to save in database
              linkUrl: summary.musicUrl || "",      // Original Spotify URL
              artistName: summary.musicArtist || "", // Artist name
              songName: summary.musicTitle || "",    // Song title
              videoId: uploadedVideoIds[i] // Different video for each ad
            };

            try {
              console.log(`Creating ad ${i + 1}/${uploadedVideoIds.length} with videoId: ${uploadedVideoIds[i]}`);
              const step3Response = await createAdStep3(step3Payload).unwrap();
              console.log(`Ad ${i + 1} created successfully:`, step3Response);
              successfulAds.push(step3Response);
            } catch (step3Error: any) {
              console.error(`Ad ${i + 1} creation failed:`, step3Error);

              // Prefer actual API/Meta error over generic msg for notification
              let errorMessage = "Failed to create ad";
              if (step3Error?.data) {
                errorMessage = step3Error.data.error ||
                              step3Error.data.message ||
                              step3Error.data.error_description ||
                              step3Error.data.msg ||
                              errorMessage;
              } else if (step3Error?.message) {
                errorMessage = step3Error.message;
              }

              // Handle specific ad creation errors
              if (errorMessage.includes('creative') || errorMessage.includes('Creative')) {
                errorMessage = `Creative error: ${errorMessage}`;
              } else if (errorMessage.includes('page') || errorMessage.includes('Page')) {
                errorMessage = `Facebook page error: ${errorMessage}`;
              } else if (errorMessage.includes('video') || errorMessage.includes('Video')) {
                errorMessage = `Video error: ${errorMessage}`;
              } else if (errorMessage.includes('instagram') || errorMessage.includes('Instagram')) {
                errorMessage = `Instagram account error: ${errorMessage}`;
              }

              failedAds.push({
                videoId: uploadedVideoIds[i],
                error: errorMessage
              });
            }
          }

          // Final step - Submitting to Facebook & Instagram (100%)
          updateStep(6, true, 100);

          // Handle results
          if (successfulAds.length > 0) {
            // Set success state to show congratulations popup
            setIsSubmissionSuccess(true);
            setSuccessCampaignId(successfulAds[0].summary?.campaignId);
            setShowSubmittingModal(true);

            // Show warning if some ads failed
            if (failedAds.length > 0) {
              toast.warning(`Created ${successfulAds.length} ad(s) successfully. ${failedAds.length} ad(s) failed to create.`);
            }
          } else {
            // All ads failed
            const errorMessage = failedAds.length > 0
              ? failedAds.map(f => f.error).join('; ')
              : "Failed to create any ads";

            // Enhance the error message for better user experience
            const enhancedErrorMessage = `Ad creation failed: ${errorMessage}`;

            toast.error(enhancedErrorMessage, {
              autoClose: 12000,
              hideProgressBar: false,
            });
            setShowSubmittingModal(false);
            setIsSubmissionError(true);
            setSubmissionErrorMessage(enhancedErrorMessage);
            return;
          }

          // Get Instagram username from summary for success message
          if (summary.instagramName) {
            setSuccessInstagramUsername(summary.instagramName);
          }


          // Delete draft when campaign was created from a restored draft
          const draftToDelete = sessionStorage.getItem("currentDraftId") || draftId || null;
          if (draftToDelete) {
            try {
              await deleteDraftCampaign(draftToDelete).unwrap();
              console.log("Draft deleted successfully after campaign creation:", draftToDelete);
              sessionStorage.removeItem("currentDraftId");
            } catch (draftDeleteError) {
              console.error("Failed to delete draft:", draftDeleteError);
              // Don't fail the entire flow if draft deletion fails
              // The campaign was created successfully, so we still show success
            }
          }

          // Note: No toast notification needed since we're showing the success modal

          // Optionally navigate or reset form
          // You can add navigation logic here if needed
        } else {
          // No payment method - show payment required modal
          setShowSubmittingModal(false);
          setShowPaymentRequiredModal(true);
          return;
        }
      } else {
        console.warn("No ad account ID from summary, skipping payment method check");
      }

    } catch (error: any) {
      // Enhanced error message extraction
      let errorMessage = "Failed to create campaign";

      if (error?.data) {
        errorMessage = error.data.msg ||
                      error.data.message ||
                      error.data.error ||
                      error.data.error_description ||
                      errorMessage;
      } else if (error?.message) {
        errorMessage = error.message;
      } else if (error?.error) {
        errorMessage = error.error;
      }

      // Add context for different error types
      if (errorMessage.includes('network') || errorMessage.includes('Network')) {
        errorMessage = `Network error: Please check your connection and try again.`;
      } else if (errorMessage.includes('timeout') || errorMessage.includes('timed out')) {
        errorMessage = `Request timeout: The operation took too long. Please try again.`;
      } else if (errorMessage.includes('server') || errorMessage.includes('Server')) {
        errorMessage = `Server error: Please try again later.`;
      } else if (errorMessage.includes('authorization') || errorMessage.includes('Authorization')) {
        errorMessage = `Authorization error: Please log in again and try.`;
      }

      // Show enhanced toast notification
      toast.error(errorMessage, {
        autoClose: 10000,
        hideProgressBar: false,
        closeButton: true,
      });

      // Set error state for modal
      setIsSubmissionError(true);
      setSubmissionErrorMessage(errorMessage);

      // Log error for debugging
      console.error("Campaign creation error:", error);

      // Don't reset progress - let user see how far we got
    }
  };

  const handleSaveDraft = async () => {
    try {
      setIsSavingDraft(true);

      // Validate required fields
      if (!summary.adName) {
        toast.error("Campaign name is required");
        return;
      }

      if (!summary.adAccountId) {
        toast.error("Ad account is required");
        return;
      }

      // Prepare campaign data
      const campaignData = {
        adName: summary.adName,
        spotifyUrl: summary.musicUrl || "",
        artistName: summary.musicArtist || "",
        title: summary.musicTitle || "",
        selectedCountry: summary.country || "", // Keep tier name as is
        selectedInterests: summary.interests || [],
        audienceAge: summary.audienceAge || "",
        audienceGender: summary.audienceGender || "",
        campaignBudget: summary.campaignBudget,
        campaignStartDate: summary.campaignStartDate || "",
        campaignEndDate: summary.campaignEndDate || "",
        isScheduled: summary.isScheduled || "always",
        timezone: summary.timezone || "UTC",
        startDate: summary.startDate ? summary.startDate.format("YYYY-MM-DD") : null,
        startTime: summary.startTime || "00:00",
        endDate: summary.endDate ? summary.endDate.format("YYYY-MM-DD") : null,
        endTime: summary.endTime || "00:00",
        adText: summary.adText || "",
        adHeadline: summary.adHeadline || "",
        pageId: summary.pageId || null,
        adAccountId: summary.adAccountId || null,
        // Send the Instagram ID shown in the confirmation dropdown (summary)
        instagramId: summary.instagramId || null,
        pixelId: summary.pixelId || null,
        // Add names for draft restoration
        adAccountName: summary.adAccountName || null,
        pageName: summary.pageName || null,
        instagramName: summary.instagramName || null,
        pixelName: summary.pixelName || null,
      };

      // Create FormData
      const formData = new FormData();
      formData.append('campaignData', JSON.stringify(campaignData));

      // Add videos if any
      if (summary.videos && summary.videos.length > 0) {
        const videoFiles = summary.videos
          .filter(v => v.file && !v.isUploading)
          .map(v => v.file!);

        videoFiles.forEach((file) => {
          formData.append('videos', file);
        });

        console.log(`Adding ${videoFiles.length} video(s) to draft`);
      }

      // Check if we're editing an existing draft
      const currentDraftId = sessionStorage.getItem("currentDraftId");
      const isEditing = !!currentDraftId;

      console.log(`${isEditing ? 'Updating' : 'Creating'} draft:`, { draftId: currentDraftId });

      // Use RTK Query mutations instead of fetch
      let result;
      if (isEditing && currentDraftId) {
        result = await updateDraftCampaign({ draftId: currentDraftId, formData }).unwrap();
      } else {
        result = await saveDraftCampaign(formData).unwrap();
      }

      console.log("Draft saved successfully:", result);

      // Show success modal instead of toast
      setSubmissionMode("draft");
      setIsSubmissionSuccess(true);
      setSuccessCampaignId(result.draftId);
      setShowSubmittingModal(true);

    } catch (error: any) {
      console.error("Error saving draft:", error);

      // Enhanced error message extraction
      let errorMessage = "Failed to save draft";

      if (error?.data) {
        errorMessage = error.data.msg ||
                      error.data.message ||
                      error.data.error ||
                      error.data.error_description ||
                      errorMessage;
      } else if (error?.message) {
        errorMessage = error.message;
      }

      // Add context for different error types
      if (errorMessage.includes('network') || errorMessage.includes('Network')) {
        errorMessage = `Network error: Please check your connection and try again.`;
      } else if (errorMessage.includes('storage') || errorMessage.includes('Storage')) {
        errorMessage = `Storage error: Unable to save draft. Please try again.`;
      } else if (errorMessage.includes('file') || errorMessage.includes('File')) {
        errorMessage = `File upload error: ${errorMessage}`;
      }

      toast.error(errorMessage, {
        autoClose: 8000,
        hideProgressBar: false,
      });
    } finally {
      setIsSavingDraft(false);
    }
  };

  const genreChips = summary.genres && summary.genres.length ? summary.genres : [];
  const interestChips =
    summary.interests && summary.interests.length ? summary.interests.map(i => i.name) : [];
  // Split countries by comma and trim each country name
  const countryChips = summary.country
    ? summary.country.split(',').map(c => c.trim()).filter(c => c.length > 0)
    : [];

  // Get country names from tier names or ISO codes
  const countryNames = getCountryNames(summary.country || null);

  // Check if countries form a complete tier - if so, show tier name instead of individual countries
  const getDisplayCountries = () => {
    if (!summary.country) return [];

    const countries = countryNames;
    if (countries.length === 0) return [];

    // Check if these are all Tier One countries
    const tierOneCountries = TIER_COUNTRIES["tier-one"];
    const isAllTierOne = countries.length === tierOneCountries.length &&
      countries.every(c => tierOneCountries.includes(c));

    if (isAllTierOne) return ["Tier One Countries"];

    // Check if these are all Tier Two countries
    const tierTwoCountries = TIER_COUNTRIES["tier-two"];
    const isAllTierTwo = countries.length === tierTwoCountries.length &&
      countries.every(c => tierTwoCountries.includes(c));

    if (isAllTierTwo) return ["Tier Two Countries"];

    // Check if these are all Tier Three countries
    const tierThreeCountries = TIER_COUNTRIES["tier-three"];
    const isAllTierThree = countries.length === tierThreeCountries.length &&
      countries.every(c => tierThreeCountries.includes(c));

    if (isAllTierThree) return ["Tier Three Countries"];

    // Check if these are Tier One + Two combined
    const tierOneAndTwoCountries = [...tierOneCountries, ...tierTwoCountries];
    const isAllTierOneAndTwo = countries.length === tierOneAndTwoCountries.length &&
      countries.every(c => tierOneAndTwoCountries.includes(c));

    if (isAllTierOneAndTwo) return ["Tier One and Two Countries"];

    // Otherwise show individual country names
    return countries;
  };

  const displayCountries = getDisplayCountries();

  const songValue =
    summary.musicTitle || summary.musicArtist || summary.musicUrl
      ? `${summary.musicTitle ?? ""}${summary.musicArtist ? ` by ${summary.musicArtist}` : ""}${summary.musicUrl ? " on Spotify" : ""
      }`
      : "Not provided";
  const audioStatus = summary.audioFileName
    ? summary.isAudioUploading
      ? `${summary.audioFileName} (uploading ${summary.audioUploadProgress ?? 0}%)`
      : summary.audioFileName
    : "Not uploaded";
  const videoStatus = summary.videos && summary.videos.length > 0
    ? summary.videos.map(v =>
      v.isUploading
        ? `${v.fileName} (uploading ${v.uploadProgress ?? 0}%)`
        : v.fileName
    ).join(", ")
    : "Not uploaded";
  const landingStatus = summary.landingFileName
    ? summary.isLandingUploading
      ? `${summary.landingFileName} (uploading ${summary.landingUploadProgress ?? 0}%)`
      : summary.landingFileName
    : "Not uploaded";

  return (
    <div className="w-full">
      <div
        className="relative rounded-[20px] shadow-[0_14px_40px_rgba(0,0,0,0.25)] overflow-hidden"
        style={{
          borderRadius: "20px",
          background: "#878DA11A",
          backgroundImage: `url(${ellipseBg})`,
          backgroundSize: "100% 100%",
          backgroundPosition: "center",
          backgroundRepeat: "no-repeat",
        }}
      >
        <div
          className="flex items-center gap-3 cursor-pointer hover:opacity-90 transition-opacity"
          style={{
            height: "88px",
            padding: "24px",
          }}
          onClick={() => onToggle(id)}
        >
          <div className="flex items-center gap-3 flex-1">
            <img src={isOpen ? dropOpenIcon : stepTenIcon} alt="Step 10" className="w-8 h-8" />
            <span
              style={{
                fontFamily: "Poppins",
                fontWeight: 400,
                fontStyle: "normal",
                fontSize: "20px",
                lineHeight: "130%",
                letterSpacing: "0%",
                textAlign: "center",
                color: isOpen ? "#58F2A9" : "#FFFFFF",
              }}
            >
              Confirmation Summary
            </span>
          </div>
          <img
            src={downArrow}
            alt="Toggle"
            className="pointer-events-none transition-transform"
            style={{
              height: "9.5px",
              width: "auto",
              transform: isOpen ? "rotate(180deg)" : "rotate(0deg)",
              filter: isOpen
                ? "brightness(0) saturate(100%) invert(78%) sepia(89%) saturate(1352%) hue-rotate(90deg) brightness(102%) contrast(101%)"
                : "none",
            }}
          />
        </div>

        {isOpen && (
          <div
            className="px-6 pb-6 flex flex-col gap-6"
          >
            {/* Confirmation Summary */}
            <div className="flex flex-col max-w-[99%] ml-[44px]">
              {/* Objective */}
              <div className="flex items-center justify-between pb-3 border-b border-[#878DA133]">
                <span className="text-white [font-family:'Poppins',Helvetica]" style={{ fontSize: "14px", fontWeight: 400, lineHeight: "130%" }}>
                  Objective:
                </span>
                <span className="text-white [font-family:'Poppins',Helvetica]" style={{ fontSize: "14px", fontWeight: 400, lineHeight: "130%" }}>
                  Grow my Spotify track
                </span>
              </div>

              {/* Facebook Ads account */}
              <div className="flex items-center justify-between pt-3 pb-3 border-b border-[#878DA133]">
                <span className="text-white [font-family:'Poppins',Helvetica]" style={{ fontSize: "14px", fontWeight: 400, lineHeight: "130%" }}>
                  Facebook Ads account:
                </span>
                <span className="text-white [font-family:'Poppins',Helvetica]" style={{ fontSize: "14px", fontWeight: 400, lineHeight: "130%", maxWidth: "65%", textAlign: "right" }}>
                  {summary.adAccountName && summary.adAccountName.trim().length ? summary.adAccountName : "Not selected"}
                </span>
              </div>

              {/* Facebook fan page */}
              <div className="flex items-center justify-between pt-3 pb-3 border-b border-[#878DA133]">
                <span className="text-white [font-family:'Poppins',Helvetica]" style={{ fontSize: "14px", fontWeight: 400, lineHeight: "130%" }}>
                  Facebook fan page:
                </span>
                <span className="text-white [font-family:'Poppins',Helvetica]" style={{ fontSize: "14px", fontWeight: 400, lineHeight: "130%", maxWidth: "65%", textAlign: "right" }}>
                  {summary.pageName && summary.pageName.trim().length
                    ? summary.pageId
                      ? `${summary.pageName} (ID: ${summary.pageId})`
                      : summary.pageName
                    : "Not selected"}
                </span>
              </div>

              {/* Instagram account */}
              <div className="flex items-center justify-between pt-3 pb-3 border-b border-[#878DA133]">
                <span className="text-white [font-family:'Poppins',Helvetica]" style={{ fontSize: "14px", fontWeight: 400, lineHeight: "130%" }}>
                  Instagram account:
                </span>
                <span className="text-white [font-family:'Poppins',Helvetica]" style={{ fontSize: "14px", fontWeight: 400, lineHeight: "130%", maxWidth: "65%", textAlign: "right" }}>
                  {summary.instagramName && summary.instagramName.trim().length
                    ? summary.instagramId
                      ? `${summary.instagramName} (ID: ${summary.instagramId})`
                      : summary.instagramName
                    : "Not selected"}
                </span>
              </div>

              {/* Song */}
              <div className="flex items-center justify-between pt-3 pb-3 border-b border-[#878DA133]">
                <span className="text-white [font-family:'Poppins',Helvetica]" style={{ fontSize: "14px", fontWeight: 400, lineHeight: "130%" }}>
                  Song:
                </span>
                <span className="text-white [font-family:'Poppins',Helvetica]" style={{ fontSize: "14px", fontWeight: 400, lineHeight: "130%" }}>
                  {songValue && songValue.trim().length ? songValue : "Not provided"}
                </span>
              </div>

              {/* Audio file */}
              {/* <div className="flex items-center justify-between pt-3 pb-3 border-b border-[#878DA133]">
                <span className="text-white [font-family:'Poppins',Helvetica]" style={{ fontSize: "14px", fontWeight: 400, lineHeight: "130%" }}>
                  Audio:
                </span>
                <span className="text-white [font-family:'Poppins',Helvetica]" style={{ fontSize: "14px", fontWeight: 400, lineHeight: "130%", maxWidth: "65%", wordBreak: "break-word" }}>
                  {audioStatus}
                </span>
              </div> */}

              {/* Video file */}
              <div className="flex items-center justify-between pt-3 pb-3 border-b border-[#878DA133]">
                <span className="text-white [font-family:'Poppins',Helvetica]" style={{ fontSize: "14px", fontWeight: 400, lineHeight: "130%" }}>
                  Video:
                </span>
                <span className="text-white [font-family:'Poppins',Helvetica]" style={{ fontSize: "14px", fontWeight: 400, lineHeight: "130%", maxWidth: "65%", wordBreak: "break-word" }}>
                  {videoStatus}
                </span>
              </div>

              {/* Landing file */}
              {/* <div className="flex items-center justify-between pt-3 pb-3 border-b border-[#878DA133]">
                <span className="text-white [font-family:'Poppins',Helvetica]" style={{ fontSize: "14px", fontWeight: 400, lineHeight: "130%" }}>
                  Landing:
                </span>
                <span className="text-white [font-family:'Poppins',Helvetica]" style={{ fontSize: "14px", fontWeight: 400, lineHeight: "130%", maxWidth: "65%", wordBreak: "break-word" }}>
                  {landingStatus}
                </span>
              </div> */}

              {/* Genre */}
              {/* <div className="flex items-center justify-between pt-3 pb-3 border-b border-[#878DA133]">
                <span className="text-white [font-family:'Poppins',Helvetica]" style={{ fontSize: "14px", fontWeight: 400, lineHeight: "130%" }}>
                  Genre:
                </span>
                <div className="flex flex-wrap gap-2 justify-end max-w-[65%]">
                  {genreChips.length ? (
                    genreChips.map((g) => (
                      <span
                        key={g}
                        className="inline-flex items-center px-3 py-1 rounded-full text-white [font-family:'Poppins',Helvetica]"
                        style={{
                          fontSize: "14px",
                          fontWeight: 400,
                          backgroundColor: "#2B313C",
                          border: "1px solid #3D4554",
                        }}
                      >
                        {g}
                      </span>
                    ))
                  ) : (
                    <span className="text-white [font-family:'Poppins',Helvetica]" style={{ fontSize: "14px", fontWeight: 400 }}>
                      Not provided
                    </span>
                  )}
                </div>
              </div> */}

              {/* Audience countries */}
              <div className="flex items-start justify-between pt-3 pb-3 border-b border-[#878DA133]">
                <span className="text-white [font-family:'Poppins',Helvetica]" style={{ fontSize: "14px", fontWeight: 400, lineHeight: "130%" }}>
                  Audience countries:
                </span>
                <div className="flex flex-col gap-2 items-end max-w-[65%]">
                  <div className="flex flex-wrap gap-2 justify-end">
                    {displayCountries.length ? (
                      displayCountries.map((country) => (
                        <span
                          key={country}
                          className="inline-flex items-center px-3 py-1 rounded-full text-white [font-family:'Poppins',Helvetica]"
                          style={{
                            fontSize: "14px",
                            fontWeight: 400,
                            backgroundColor: "rgba(255, 255, 255, 0.1)",
                          }}
                        >
                          {country}
                        </span>
                      ))
                    ) : (
                      <span className="text-white [font-family:'Poppins',Helvetica]" style={{ fontSize: "14px", fontWeight: 400 }}>
                        Not selected
                      </span>
                    )}
                  </div>
                </div>
              </div>

              {/* Audience interests */}
              <div className="flex items-start justify-between pt-3 pb-3 border-b border-[#878DA133]">
                <span className="text-white [font-family:'Poppins',Helvetica]" style={{ fontSize: "14px", fontWeight: 400, lineHeight: "130%" }}>
                  Audience interests:
                </span>
                <div className="flex flex-wrap gap-2 justify-end max-w-[65%]">
                  {interestChips.length ? (
                    interestChips.map((interest) => (
                      <span
                        key={interest}
                        className="inline-flex items-center px-3 py-1 rounded-full text-white [font-family:'Poppins',Helvetica]"
                        style={{
                          fontSize: "14px",
                          fontWeight: 400,
                          backgroundColor: "#2B313C",
                          border: "1px solid #3D4554",
                        }}
                      >
                        {interest}
                      </span>
                    ))
                  ) : (
                    <span className="text-white [font-family:'Poppins',Helvetica]" style={{ fontSize: "14px", fontWeight: 400 }}>
                      Not selected
                    </span>
                  )}
                </div>
              </div>

              {/* Audience age */}
              <div className="flex items-center justify-between pt-3 pb-3 border-b border-[#878DA133]">
                <span className="text-white [font-family:'Poppins',Helvetica]" style={{ fontSize: "14px", fontWeight: 400, lineHeight: "130%" }}>
                  Audience age:
                </span>
                <span className="text-white [font-family:'Poppins',Helvetica]" style={{ fontSize: "14px", fontWeight: 400, lineHeight: "130%" }}>
                  {summary.audienceAge || "Not selected"}
                </span>
              </div>

              {/* Audience gender */}
              <div className="flex items-center justify-between pt-3 pb-3 border-b border-[#878DA133]">
                <span className="text-white [font-family:'Poppins',Helvetica]" style={{ fontSize: "14px", fontWeight: 400, lineHeight: "130%" }}>
                  Audience gender:
                </span>
                <span className="text-white [font-family:'Poppins',Helvetica]" style={{ fontSize: "14px", fontWeight: 400, lineHeight: "130%" }}>
                  {summary.audienceGender || "Not selected"}
                </span>
              </div>

              {/* Campaign start date */}
              <div className="flex items-center justify-between pt-3 pb-3 border-b border-[#878DA133]">
                <span className="text-white [font-family:'Poppins',Helvetica]" style={{ fontSize: "14px", fontWeight: 400, lineHeight: "130%" }}>
                  Campaign start date:
                </span>
                <span className="text-white [font-family:'Poppins',Helvetica]" style={{ fontSize: "14px", fontWeight: 400, lineHeight: "130%" }}>
                  {summary.campaignStartDate || "None (run when published)"}
                </span>
              </div>

              {/* Campaign end date */}
              <div className="flex items-center justify-between pt-3 pb-3 border-b border-[#878DA133]">
                <span className="text-white [font-family:'Poppins',Helvetica]" style={{ fontSize: "14px", fontWeight: 400, lineHeight: "130%" }}>
                  Campaign end date:
                </span>
                <span className="text-white [font-family:'Poppins',Helvetica]" style={{ fontSize: "14px", fontWeight: 400, lineHeight: "130%" }}>
                  {summary.campaignEndDate || "None (run until turned off manually)"}
                </span>
              </div>

              {/* Campaign budget */}
              <div className="flex items-center justify-between pt-3 pb-3 border-b border-[#878DA133]">
                <span className="text-white [font-family:'Poppins',Helvetica]" style={{ fontSize: "14px", fontWeight: 400, lineHeight: "130%" }}>
                  Campaign budget:
                </span>
                <span className="text-white [font-family:'Poppins',Helvetica]" style={{ fontSize: "14px", fontWeight: 400, lineHeight: "130%" }}>
                  {summary.currency} {summary.campaignBudget}/day
                </span>
              </div>

              {/* Campaign Item */}
              <div className="flex items-center justify-between pt-3">
                <span className="text-white [font-family:'Poppins',Helvetica]" style={{ fontSize: "14px", fontWeight: 400, lineHeight: "130%" }}>
                  Campaign Item:
                </span>
                <div className="flex flex-wrap gap-2">
                  <span
                    className="inline-flex items-center px-3 py-1 rounded-full text-white [font-family:'Poppins',Helvetica]"
                    style={{
                      fontSize: "14px",
                      fontWeight: 400,
                      backgroundColor: "#2B313C",
                      border: "1px solid #3D4554"
                    }}
                  >
                    Track
                  </span>
                </div>
              </div>
            </div>

            {/* Legal Section */}
            <div className="flex flex-col gap-4 max-w-[99%] ml-[44px]">
              <p
                className="text-white [font-family:'Poppins',Helvetica]"
                style={{
                  fontSize: "14px",
                  fontWeight: 400,
                  lineHeight: "130%",
                }}
              >
                Your campaign is almost live! There's just some quick legal stuff
                we need before we can save and publish your campaign:
              </p>

              <div className="flex flex-col gap-3">
                {/* Checkbox 1 */}
                <label className="flex items-start gap-3 cursor-pointer">
                  <div className="relative flex-shrink-0 mt-0.5">
                    <input
                      type="checkbox"
                      checked={checkbox1}
                      onChange={(e) => setCheckbox1(e.target.checked)}
                      className="sr-only"
                    />
                    <div
                      className={`w-5 h-5 flex items-center justify-center transition-colors ${checkbox1
                        ? "bg-[#58F2A9]"
                        : "bg-transparent border-2 border-[#878DA199]"
                        }`}
                      style={{
                        borderRadius: "4px",
                        borderWidth: checkbox1 ? "0" : "2px",
                      }}
                    >
                      {checkbox1 && (
                        <svg
                          width="12"
                          height="12"
                          viewBox="0 0 12 12"
                          fill="none"
                          xmlns="http://www.w3.org/2000/svg"
                        >
                          <path
                            d="M10 3L4.5 8.5L2 6"
                            stroke="white"
                            strokeWidth="2"
                            strokeLinecap="round"
                            strokeLinejoin="round"
                          />
                        </svg>
                      )}
                    </div>
                  </div>
                  <span
                    className="text-white [font-family:'Poppins',Helvetica] flex-1"
                    style={{
                      fontSize: "16px",
                      fontWeight: 400,
                      fontStyle: "normal",
                      lineHeight: "130%",
                      letterSpacing: "0%",
                    }}
                  >
                    I confirm my campaign settings are correct and that I want to
                    create a Facebook ad campaign with these settings
                  </span>
                </label>

                {/* Checkbox 2 */}
                <label className="flex items-start gap-3 cursor-pointer">
                  <div className="relative flex-shrink-0 mt-0.5">
                    <input
                      type="checkbox"
                      checked={checkbox2}
                      onChange={(e) => setCheckbox2(e.target.checked)}
                      className="sr-only"
                    />
                    <div
                      className={`w-5 h-5 flex items-center justify-center transition-colors ${checkbox2
                        ? "bg-[#58F2A9]"
                        : "bg-transparent border-2 border-[#878DA199]"
                        }`}
                      style={{
                        borderRadius: "4px",
                        borderWidth: checkbox2 ? "0" : "2px",
                      }}
                    >
                      {checkbox2 && (
                        <svg
                          width="12"
                          height="12"
                          viewBox="0 0 12 12"
                          fill="none"
                          xmlns="http://www.w3.org/2000/svg"
                        >
                          <path
                            d="M10 3L4.5 8.5L2 6"
                            stroke="white"
                            strokeWidth="2"
                            strokeLinecap="round"
                            strokeLinejoin="round"
                          />
                        </svg>
                      )}
                    </div>
                  </div>
                  <span
                    className="text-white [font-family:'Poppins',Helvetica] flex-1"
                    style={{
                      fontSize: "16px",
                      fontWeight: 400,
                      fontStyle: "normal",
                      lineHeight: "130%",
                      letterSpacing: "0%",
                    }}
                  >
                    I understand that Facebook will directly charge my ad budget to
                    my payment method connected to my Facebook ads account
                  </span>
                </label>

                {/* Checkbox 3 */}
                <label className="flex items-start gap-3 cursor-pointer">
                  <div className="relative flex-shrink-0 mt-0.5">
                    <input
                      type="checkbox"
                      checked={checkbox3}
                      onChange={(e) => setCheckbox3(e.target.checked)}
                      className="sr-only"
                    />
                    <div
                      className={`w-5 h-5 flex items-center justify-center transition-colors ${checkbox3
                        ? "bg-[#58F2A9]"
                        : "bg-transparent border-2 border-[#878DA199]"
                        }`}
                      style={{
                        borderRadius: "4px",
                        borderWidth: checkbox3 ? "0" : "2px",
                      }}
                    >
                      {checkbox3 && (
                        <svg
                          width="12"
                          height="12"
                          viewBox="0 0 12 12"
                          fill="none"
                          xmlns="http://www.w3.org/2000/svg"
                        >
                          <path
                            d="M10 3L4.5 8.5L2 6"
                            stroke="white"
                            strokeWidth="2"
                            strokeLinecap="round"
                            strokeLinejoin="round"
                          />
                        </svg>
                      )}
                    </div>
                  </div>
                  <span
                    className="text-white [font-family:'Poppins',Helvetica] flex-1"
                    style={{
                      fontSize: "16px",
                      fontWeight: 400,
                      fontStyle: "normal",
                      lineHeight: "130%",
                      letterSpacing: "0%",
                    }}
                  >
                    I understand that PitchPlaylists is not responsible for Facebook
                    ad results and does not offer refunds for any
                  </span>
                </label>
              </div>
            </div>

            {/* Footer Buttons */}
            <div className="flex items-center justify-end gap-3 pt-4">
              <button
                type="button"
                onClick={() => window.location.href = '/meta-ads-builder'}
                className="text-white hover:opacity-70 transition-opacity"
                style={{
                  fontFamily: "Poppins",
                  fontSize: "16px",
                  fontWeight: 400,
                  textDecoration: "underline",
                }}
              >
                Cancel
              </button>

              {!isEditMode && (
                <button
                  type="button"
                  onClick={handleSaveDraft}
                  disabled={isSavingDraft}
                  className="inline-flex items-center rounded-[999px] hover:opacity-90 transition-opacity disabled:opacity-50 disabled:cursor-not-allowed"
                  style={{
                    width: "164px",
                    height: "48px",
                    gap: "8px",
                    padding: "8px 24px",
                    background: "#FFFFFF",
                    color: "#020202",
                    fontWeight: 600, // SemiBold
                    fontStyle: "normal",
                    fontSize: "14px",
                    lineHeight: "100%",
                    letterSpacing: "0%",
                  }}
                >
                  <img
                    src={draftIcon}
                    alt="Draft"
                    style={{ width: "20px", height: "20px" }}
                  />
                  {isSavingDraft ? "Saving..." : "Save draft"}
                </button>
              )}

              <button
                type="button"
                onClick={isEditMode ? handleUpdateCampaign : handleCreateCampaign}
                disabled={isCreating || !checkbox1 || !checkbox2 || !checkbox3}
                className="inline-flex items-center rounded-[999px] text-white hover:opacity-90 transition-opacity disabled:opacity-50 disabled:cursor-not-allowed"
                style={{
                  whiteSpace: "nowrap",
                  height: "48px",
                  padding: "8px 8px 8px 24px",
                  gap: "32px",
                  fontWeight: 600,
                  fontStyle: "normal",
                  fontSize: "16px",
                  lineHeight: "100%",
                  letterSpacing: "0%",
                  backgroundImage: `url(${createButtonBg})`,
                  backgroundSize: "cover",
                  backgroundPosition: "center",
                  backgroundRepeat: "no-repeat",
                  backgroundColor: "transparent",
                  border: "none",
                }}
              >
                {isCreating ? (isEditMode ? "Updating..." : "Creating...") : (isEditMode ? "Update" : "Create")}
                {!isCreating && <img src={createIcon} alt={isEditMode ? "Update" : "Create"} />}
              </button>

            </div>
          </div>
        )}
      </div>

      {/* Eligibility Modal */}
      {showEligibilityModal && eligibilityData && (
        <MetaAdsEligibilityModal
          isOpen={showEligibilityModal}
          eligible={eligibilityData.eligible}
          reason={eligibilityData.reason}
          checks={eligibilityData.checks}
          recommendations={eligibilityData.recommendations}
          onClose={() => {
            setShowEligibilityModal(false);
            setEligibilityData(null);
          }}
        />
      )}

      {/* Submitting Modal */}
      <MetaAdsSubmittingModal
        isOpen={showSubmittingModal}
        progress={progress}
        steps={submissionSteps}
        isSuccess={isSubmissionSuccess}
        isError={isSubmissionError}
        errorMessage={submissionErrorMessage}
        instagramUsername={successInstagramUsername}
        campaignId={successCampaignId}
        mode={submissionMode}
        onClose={() => {
          if (isSubmissionSuccess && (submissionMode === "draft" || submissionMode === "create" || submissionMode === "update")) {
            window.location.href = '/campaigns-list';
            return; // Don't execute anything else, just redirect
          }

        }}
      />

      {/* Payment Required Modal */}
      <MetaAdsPaymentRequiredModal
        isOpen={showPaymentRequiredModal}
        onConfirm={() => {
          setShowPaymentRequiredModal(false);
        }}
      />
    </div>
  );
};

export default ConfirmationDropdown;

